import { SearchExamCoursesDocument, useEnableExamCourseByIdMutation } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AdminExamCourseEnablementList } from '../admin-exam-course-enablement-list';

const AdminExamCourseEnablementSchema = z.object({
  examCourseId: z.string(),
});
type AdminExamCourseEnablementFormInput = z.infer<typeof AdminExamCourseEnablementSchema>;

interface AdminExamCourseEnablementModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AdminExamCourseEnablementModal(
  props: Readonly<AdminExamCourseEnablementModalProps>,
) {
  const { open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [enableExamCourseById] = useEnableExamCourseByIdMutation();

  const formContext = useForm<AdminExamCourseEnablementFormInput>({
    defaultValues: {
      examCourseId: undefined,
    },
    resolver: zodResolver(AdminExamCourseEnablementSchema),
  });

  const handleAddExamCourse = async (data: AdminExamCourseEnablementFormInput) => {
    await enableExamCourseById({
      variables: {
        enableExamCourseByIdInput: {
          id: data.examCourseId,
        },
      },
      refetchQueries: [SearchExamCoursesDocument],
      awaitRefetchQueries: true,
      onCompleted: (res) => {
        showSnackbar({
          message: 'เพิ่มหลักสูตรสำเร็จ',
          variant: 'success',
        });
        const examCourseCode = res.ecEnableExamCourseById.examCourse.code;

        navigate('/admin/exam-courses/:examCourseCode/details', {
          params: { examCourseCode },
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เพิ่มหลักสูตรไม่สำเร็จ',
          variant: 'error',
        });
      },
    });
  };

  const handleSelectRow = (selectedExamCourse: string | undefined) => {
    if (selectedExamCourse) {
      formContext.setValue('examCourseId', selectedExamCourse, { shouldDirty: true });
    } else {
      formContext.reset();
    }
  };

  const handleClose = () => {
    onClose();
    formContext.reset();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth='lg'
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleAddExamCourse) },
        }}
      >
        <DialogTitle onClose={handleClose}>เพิ่มหลักสูตร</DialogTitle>
        <Divider />
        <DialogContent>
          <AdminExamCourseEnablementList onSelectRow={handleSelectRow} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
            ยกเลิก
          </Button>
          <FormButton label='เพิ่มหลักสูตร' size='large' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
